import { Component,
  EventEmitter,
  OnDestroy,
  OnInit,
  Output,
  ChangeDetectorRef,
  ChangeDetectionStrategy, inject, DestroyRef } from '@angular/core';
import { forkJoin, Subscription } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import * as _ from 'lodash-es';
import { GlobalService } from '../../../global/services/global.service';
import { WorkspaceService } from '../../../workspace/workspace.service';
import { AppService } from '../../../app.service';
import {
  buildHeaderDataShell,
  generalConfigFromGlobal,
  normalizeFilterParams,
  rowPayloadFromActiveScreen,
  gridPayloadFromActiveScreen,
} from '../../../shared/workspace-element-context';
import { GtwSnackbarService } from '../../../shared/_services/gtw-snackbar.service';
import { ExpenseConsolConfigService } from './expense-consol-config.service';
import { ConfirmationModalConfig } from '../../../shared/components/confirmation-modal/confirmation-modal.component';

@Component({
    selector: 'gtw-expense-consol-config',
    templateUrl: './expense-consol-config.component.html',
    styleUrls: ['./expense-consol-config.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class ExpenseConsolConfigComponent implements OnInit, OnDestroy {
  private readonly destroyRef = inject(DestroyRef);


  @Output('request-grid-update') requestGridUpdate = new EventEmitter<string>();

  private readonly subs = new Subscription();

  currentTabName = 'Form Entry';
  minimise = false;
  isSaveClicked = false;
  crudLoading = false;
  showFormLoader = true;

  private isInitialized = false;
  private isLoading = false;

  baseURLs: any = {};
  globalService: any = {};
  filterParams: any = {};
  rowData: any = {};
  gridData: any = {};
  GENERAL_CONFIG: any = {};
  USER_SETTINGS: any = {};
  SERVER_CONFIG: any = {};

  filterTaxYear: any;
  scenario: string = '';
  jcd_key: string = '';
  logged_in_user: string = '';

  filteredTaxYearData: any[] = [];
  filingGroupData: any[] = [];
  fdiiDerivedData: any[] = [];
  fdiiCodeData: any[] = [];
  taxYearsData: any[] = [];
  gieNamesDropdown: any[] = [];
  existingExpConsolData: any[] = [];
  is_locked: any = null;

  operationType: 'I' | 'U' = 'I';
  showDelete = true;
  label1: string = '';
  label2: string = '';

  selectedTaxYear: string = '';
  selectedAdjKey: string | null = null;
  selectedFilingGroup: string = '';
  selectedGIECodeKey: string = '';
  fdiiAmt: number | string = '';
  giltiAmt: number | string = '';

  modalTitle = 'Expense A&A Configuration';

  showConfirmDialog = false;
  deleteModalConfig: ConfirmationModalConfig = { title: '', message: '' };

  constructor(
    private gtwGlobal: GlobalService,
    private gtwWorkspace: WorkspaceService,
    private gtwApp: AppService,
    private gtwSnackbarService: GtwSnackbarService,
    private cdr: ChangeDetectorRef,
    private expenseConsolConfigService: ExpenseConsolConfigService
  ) {}

  ngOnInit(): void {
    this.applyWorkspaceContext();
    this.subs.add(this.gtwWorkspace.setTabActive.subscribe(() => {
      this.applyWorkspaceContext();
      this.tryInit();
    }));
    this.subs.add(this.gtwGlobal.globalParamsChanged$.subscribe(() => {
      this.applyWorkspaceContext();
      this.tryInit();
    }));
    this.tryInit();
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  private applyWorkspaceContext(): void {
    this.isInitialized = false;
    this.isLoading = false;
    const activeScreen = this.gtwWorkspace.workspaceFactory?.activeScreen || {};
    const header = buildHeaderDataShell(this.gtwGlobal, this.gtwApp);
    this.baseURLs = generalConfigFromGlobal(this.gtwGlobal);
    this.GENERAL_CONFIG = header.generalConfigData || {};
    this.globalService = header.globalParams || {};
    this.USER_SETTINGS = header.userSettingsData || {};
    this.SERVER_CONFIG = header.serverConfigData || {};
    this.logged_in_user = (this.USER_SETTINGS?.user?.sso_id) || '';
    this.filterParams = normalizeFilterParams(activeScreen);
    const row = rowPayloadFromActiveScreen(activeScreen);
    this.rowData = Array.isArray(row) ? (row[0] || {}) : row;
    this.gridData = gridPayloadFromActiveScreen(activeScreen) || {};
  }

  private tryInit(): void {
    if (this.isInitialized || this.isLoading) return;
    const baseUrl = this.GENERAL_CONFIG?.base_url || this.baseURLs?.base_url;
    if (!baseUrl || (!this.globalService && !this.GENERAL_CONFIG)) return;
    this.isInitialized = true;
    this.init();
  }

  private init(): void {
    this.showFormLoader = true;
    this.filterTaxYear = this.filterParams.tax_year ?? this.globalService?.tax_year;
    this.scenario = this.globalService?.scenario ?? this.filterParams.scenario ?? '';
    this.jcd_key = this.globalService?.jcd_key ?? this.filterParams.jcd_key ?? '';
    this.cdr.detectChanges();

    const baseUrl = this.GENERAL_CONFIG?.base_url || this.baseURLs?.base_url;
    if (!baseUrl) {
      this.showFormLoader = false;
      this.cdr.detectChanges();
      return;
    }

    const n10cpvParams = {
      action_code: 'n10cpv',
      activity_key: this.filterParams?.activity_key ?? this.globalService?.activity_key ?? 1450,
      is_issue_key_enabled: this.filterParams?.is_issue_key_enabled ?? this.globalService?.is_issue_key_enabled ?? 'N',
      is_locked: 'N',
      issue_id: this.filterParams?.issue_id ?? this.globalService?.issue_id ?? 'issue_id_for_tx_save',
      issue_short_desc: this.filterParams?.issue_short_desc ?? this.globalService?.issue_short_desc ?? '',
      jcd_key: this.jcd_key ?? this.filterParams?.jcd_key ?? this.globalService?.jcd_key ?? 250,
      jcd_ta_key: this.filterParams?.jcd_ta_key ?? this.globalService?.jcd_ta_key ?? 1257,
      p_jcd_key: this.jcd_key ?? this.filterParams?.p_jcd_key ?? this.globalService?.p_jcd_key ?? 250,
      p_scenario: this.scenario ?? this.filterParams?.p_scenario ?? this.filterParams?.scenario ?? this.globalService?.scenario ?? 74,
      scenario: this.scenario ?? this.filterParams?.scenario ?? this.globalService?.scenario ?? 74,
      scenarioCode: this.filterParams?.scenarioCode ?? this.globalService?.scenarioCode ?? 'RAF',
      screen_key: this.filterParams?.screen_key ?? this.globalService?.screen_key ?? 379,
      screen_type: this.filterParams?.screen_type ?? this.globalService?.screen_type ?? 'grid',
      tax_year: this.filterTaxYear ?? this.filterParams?.tax_year ?? this.globalService?.tax_year ?? 2024
    };

    this.expenseConsolConfigService.loadJsonObject(
      baseUrl,
      n10cpvParams,
      this.globalService
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (res: any) => {
        const jsonObj = res?.jsonObject ?? res;
        this.is_locked = jsonObj;
        if (this.is_locked && Array.isArray(this.is_locked) && this.is_locked[0]?.LOCK_STATUS === 'Y') {
          this.gtwSnackbarService.openSnackBar(
            'Tax Return has already been filed for tax year ' + (this.filterTaxYear || '') + '.',
            'gtw-snackbar--red'
          );
          this.requestGridUpdate.emit('cancel');
          this.showFormLoader = false;
          this.cdr.detectChanges();
          return;
        }
        this.loadDropdownsAndData(baseUrl);
      },
      error: () => {
        this.showFormLoader = false;
        this.cdr.detectChanges();
      }
    });
  }

  private loadDropdownsAndData(baseUrl: string): void {
    this.isLoading = true;

    const lfmzzqParams = {
      action_code: 'lfmzzq',
      activity_key: this.filterParams?.activity_key ?? this.globalService?.activity_key ?? 1450,
      is_issue_key_enabled: this.filterParams?.is_issue_key_enabled ?? this.globalService?.is_issue_key_enabled ?? 'N',
      is_locked: 'N',
      issue_id: this.filterParams?.issue_id ?? this.globalService?.issue_id ?? 'issue_id_for_tx_save',
      issue_short_desc: this.filterParams?.issue_short_desc ?? this.globalService?.issue_short_desc ?? '',
      jcd_key: this.jcd_key ?? this.filterParams?.jcd_key ?? this.globalService?.jcd_key ?? 250,
      jcd_ta_key: this.filterParams?.jcd_ta_key ?? this.globalService?.jcd_ta_key ?? 1257,
      scenario: this.scenario ?? this.filterParams?.scenario ?? this.globalService?.scenario ?? 74,
      scenarioCode: this.filterParams?.scenarioCode ?? this.globalService?.scenarioCode ?? 'RAF',
      screen_key: this.filterParams?.screen_key ?? this.globalService?.screen_key ?? 379,
      screen_type: this.filterParams?.screen_type ?? this.globalService?.screen_type ?? 'grid',
      tax_year: this.filterTaxYear ?? this.filterParams?.tax_year ?? this.globalService?.tax_year ?? 2024
    };

    const params9qgldf = {
      action_code: '9qgldf',
      activity_key: this.filterParams?.activity_key ?? this.globalService?.activity_key ?? 1450,
      is_issue_key_enabled: this.filterParams?.is_issue_key_enabled ?? this.globalService?.is_issue_key_enabled ?? 'N',
      is_locked: 'N',
      issue_id: this.filterParams?.issue_id ?? this.globalService?.issue_id ?? 'issue_id_for_tx_save',
      issue_short_desc: this.filterParams?.issue_short_desc ?? this.globalService?.issue_short_desc ?? '',
      jcd_key: this.jcd_key ?? this.filterParams?.jcd_key ?? this.globalService?.jcd_key ?? 250,
      jcd_ta_key: this.filterParams?.jcd_ta_key ?? this.globalService?.jcd_ta_key ?? 1257,
      scenario: this.scenario ?? this.filterParams?.scenario ?? this.globalService?.scenario ?? 74,
      scenarioCode: this.filterParams?.scenarioCode ?? this.globalService?.scenarioCode ?? 'RAF',
      screen_key: this.filterParams?.screen_key ?? this.globalService?.screen_key ?? 379,
      screen_type: this.filterParams?.screen_type ?? this.globalService?.screen_type ?? 'grid',
      tax_year: this.filterTaxYear ?? this.filterParams?.tax_year ?? this.globalService?.tax_year ?? 2024
    };

    const paramsGk7crv = {
      action_code: 'gk7crv',
      activity_key: this.filterParams?.activity_key ?? this.globalService?.activity_key ?? 1450,
      is_issue_key_enabled: this.filterParams?.is_issue_key_enabled ?? this.globalService?.is_issue_key_enabled ?? 'N',
      is_locked: 'N',
      issue_id: this.filterParams?.issue_id ?? this.globalService?.issue_id ?? 'issue_id_for_tx_save',
      issue_short_desc: this.filterParams?.issue_short_desc ?? this.globalService?.issue_short_desc ?? '',
      jcd_key: this.jcd_key ?? this.filterParams?.jcd_key ?? this.globalService?.jcd_key ?? 250,
      jcd_ta_key: this.filterParams?.jcd_ta_key ?? this.globalService?.jcd_ta_key ?? 1257,
      scenario: this.scenario ?? this.filterParams?.scenario ?? this.globalService?.scenario ?? 74,
      scenarioCode: this.filterParams?.scenarioCode ?? this.globalService?.scenarioCode ?? 'RAF',
      screen_key: this.filterParams?.screen_key ?? this.globalService?.screen_key ?? 379,
      screen_type: this.filterParams?.screen_type ?? this.globalService?.screen_type ?? 'grid',
      tax_year: this.filterTaxYear ?? this.filterParams?.tax_year ?? this.globalService?.tax_year ?? 2024
    };

    const codeMast$ = this.expenseConsolConfigService.loadJsonObject(baseUrl, lfmzzqParams, this.globalService);
    const gie$ = this.expenseConsolConfigService.loadJsonObject(baseUrl, params9qgldf, this.globalService);
    const filing$ = this.expenseConsolConfigService.loadJsonObject(baseUrl, paramsGk7crv, this.globalService);

    forkJoin({ codeMast: codeMast$, gie: gie$, filing: filing$ }).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (results: any) => {
        const arr = results.codeMast?.jsonObject ?? results.codeMast ?? [];
        this.taxYearsData = _.filter(arr, (p: any) => _.includes(['TAX_YEAR'], p.CODE_GROUP));
        this.fdiiCodeData = _.filter(arr, (p: any) => _.includes(['FDII_CODE'], p.CODE_GROUP));
        this.fdiiDerivedData = _.filter(this.fdiiCodeData, (p: any) =>
          _.includes(['GILTI', 'FGN Derived DEI'], p.CODE_SHORT_DESC)
        );
        const ft = Number(this.filterTaxYear);
        this.filteredTaxYearData = this.taxYearsData.filter(
          (t: any) => t.CODE_GROUP === 'TAX_YEAR' && t.CODE_DISP_DESC <= ft && t.CODE_DISP_DESC >= ft - 11
        );
        this.gieNamesDropdown = results.gie?.jsonObject ?? results.gie ?? [];
        this.filingGroupData = results.filing?.jsonObject ?? results.filing ?? [];
        this.loadSavedExpenseConsole();
        this.getSavedExpConsolConf(baseUrl);
        this.showFormLoader = false;
        this.isLoading = false;
        this.cdr.detectChanges();
      },
      error: () => {
        this.showFormLoader = false;
        this.isLoading = false;
        this.cdr.detectChanges();
      }
    });
  }

  private getSavedExpConsolConf(baseUrl: string): void {
    const paramsQnr7oy = {
      action_code: 'qnr7oy',
      activity_key: this.filterParams?.activity_key ?? this.globalService?.activity_key ?? 1450,
      is_issue_key_enabled: this.filterParams?.is_issue_key_enabled ?? this.globalService?.is_issue_key_enabled ?? 'N',
      is_locked: 'N',
      issue_id: this.filterParams?.issue_id ?? this.globalService?.issue_id ?? 'issue_id_for_tx_save',
      issue_short_desc: this.filterParams?.issue_short_desc ?? this.globalService?.issue_short_desc ?? '',
      jcd_key: this.jcd_key ?? this.filterParams?.jcd_key ?? this.globalService?.jcd_key ?? 250,
      jcd_ta_key: this.filterParams?.jcd_ta_key ?? this.globalService?.jcd_ta_key ?? 1257,
      scenario: this.scenario ?? this.filterParams?.scenario ?? this.globalService?.scenario ?? 74,
      scenarioCode: this.filterParams?.scenarioCode ?? this.globalService?.scenarioCode ?? 'RAF',
      screen_key: this.filterParams?.screen_key ?? this.globalService?.screen_key ?? 379,
      screen_type: this.filterParams?.screen_type ?? this.globalService?.screen_type ?? 'grid',
      tax_year: this.filterTaxYear ?? this.filterParams?.tax_year ?? this.globalService?.tax_year ?? 2024
    };

    this.expenseConsolConfigService.loadJsonObject(
      baseUrl,
      paramsQnr7oy,
      this.globalService
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (res: any) => {
        this.existingExpConsolData = res?.jsonObject ?? res ?? [];
        this.cdr.detectChanges();
      }
    });
  }

  private getGieName(gieKey: string): string {
    const g = _.find(this.gieNamesDropdown, (i: any) => String(i.GIE_KEY) === String(gieKey));
    return g ? String(g.GIE_NAME || '') : '';
  }

  gieNameChange(): void {
    if (this.selectedGIECodeKey) {
      if (this.getGieName(this.selectedGIECodeKey) === 'ASSET') {
        this.label1 = 'FDII';
        this.label2 = 'Section 250';
      } else {
        this.label1 = 'Foreign Derived DEI';
        this.label2 = 'GILTI';
      }
    }
    this.cdr.detectChanges();
  }

  loadSavedExpenseConsole(): void {
    const gd = this.gridData;
    const row = gd?.rowData ?? this.rowData;
    // FIX: Remove invalid object comparison, and check for plain object with keys
    if (gd && typeof gd === 'object' && Object.keys(gd).length > 0) {
      if (row && typeof row === 'object' && Object.keys(row).length > 0) {
        this.operationType = 'U';
        this.selectedTaxYear = String(row['TAX_YEAR'] ?? '');
        this.selectedAdjKey = row['ADJ_KEY'] != null ? String(row['ADJ_KEY']) : null;
        this.selectedFilingGroup = String(row['FILING_GRP_KEY'] ?? '');
        this.selectedGIECodeKey = String(row['GIE_KEY'] ?? '');
        const fgn = row['FGN_Derived_DEI_0'];
        const gilti = row['GILTI_0'];
        this.fdiiAmt = fgn !== undefined && fgn !== null && !Number.isNaN(Number(fgn)) ? Number(fgn) : '';
        this.giltiAmt = gilti !== undefined && gilti !== null && !Number.isNaN(Number(gilti)) ? Number(gilti) : '';
        this.showDelete = true;
      } else {
        this.operationType = 'I';
        this.selectedTaxYear = String(this.filterTaxYear ?? '');
        this.selectedAdjKey = null;
        this.showDelete = false;
      }
    } else {
      // Create mode: no gridData or empty - set tax year from filter/global params
      this.operationType = 'I';
      const taxYear = this.filterTaxYear ?? this.filterParams?.tax_year ?? this.globalService?.globalParams?.tax_year ?? this.globalService?.tax_year ?? '';
      this.selectedTaxYear = String(taxYear);
      this.selectedAdjKey = null;
      this.showDelete = false;
    }
    this.gieNameChange();
    this.cdr.detectChanges();
  }

  reset(): void {
    if (this.operationType === 'I') {
      this.selectedAdjKey = null;
      this.selectedFilingGroup = '';
      this.selectedGIECodeKey = '';
      this.fdiiAmt = '';
      this.giltiAmt = '';
    } else {
      this.loadSavedExpenseConsole();
      const baseUrl = this.GENERAL_CONFIG?.base_url || this.baseURLs?.base_url;
      if (baseUrl) this.getSavedExpConsolConf(baseUrl);
    }
    this.cdr.detectChanges();
  }

  cancel(): void {
    this.requestGridUpdate.emit('cancel');
  }

  save(): void {
    if (this.crudLoading) {
      this.gtwSnackbarService.openSnackBar('Please wait while we save this request', 'gtw-snackbar--yellow');
      return;
    }
    const st = this.selectedTaxYear;
    const sf = this.selectedFilingGroup;
    const sg = this.selectedGIECodeKey;
    const fa = this.fdiiAmt;
    const ga = this.giltiAmt;
    const faNum = fa === '' || fa == null ? NaN : Number(fa);
    const gaNum = ga === '' || ga == null ? NaN : Number(ga);
    if (
      st === undefined || st === null || st === '' ||
      sf === undefined || sf === null || sf === '' ||
      sg === undefined || sg === null || sg === '' ||
      fa === undefined || fa === null || fa === '' || Number.isNaN(faNum) ||
      ga === undefined || ga === null || ga === '' || Number.isNaN(gaNum)
    ) {
      this.gtwSnackbarService.openSnackBar('Please select/enter all the required fields.', 'gtw-snackbar--red');
      return;
    }
    const exists = _.find(this.existingExpConsolData, (o: any) =>
      String(o.TAX_YEAR) === String(st) &&
      String(o.FILING_GRP_KEY) === String(sf) &&
      String(o.GIE_KEY) === String(sg) &&
      String(o.ADJ_KEY) !== String(this.selectedAdjKey ?? '')
    );
    if (exists) {
      this.gtwSnackbarService.openSnackBar('The Filing Group Amounts are already configured for this selection.', 'gtw-snackbar--red');
      return;
    }

    const fgnItem = _.find(this.fdiiDerivedData, ['CODE_SHORT_DESC', 'FGN Derived DEI']);
    const giltiItem = _.find(this.fdiiDerivedData, ['CODE_SHORT_DESC', 'GILTI']);
    if (!fgnItem || !giltiItem) {
      this.gtwSnackbarService.openSnackBar('Configuration data not loaded. Please try again.', 'gtw-snackbar--red');
      return;
    }

    this.isSaveClicked = true;
    this.crudLoading = true;
    this.cdr.detectChanges();

    const clobSettingsObj = { sso_id: this.logged_in_user, scenario: this.scenario };
    const returnObj: any = {
      TAX_YEAR: st,
      scenario: this.scenario,
      ADJ_KEY: this.selectedAdjKey == null ? 0 : this.selectedAdjKey,
      FILING_GRP_KEY: sf,
      GIE_KEY: sg,
      [String(fgnItem.CODE_KEY) + '_0']: faNum,
      [String(giltiItem.CODE_KEY) + '_0']: gaNum,
      OPERATION_TYPE: this.operationType
    };
    const clobDataDetails = [returnObj];
    const baseUrl = this.GENERAL_CONFIG?.base_url || this.baseURLs?.base_url;

    this.expenseConsolConfigService.savePivotedData(
      baseUrl,
      clobSettingsObj,
      clobDataDetails,
      this.globalService,
      this.filterParams,
      this.rowData
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (result: any) => {
        this.isSaveClicked = false;
        this.crudLoading = false;
        const err = result?.errorMessage ?? result?.data?.errorMessage;
        if (err && String(err) !== 'null') {
          this.gtwSnackbarService.openSnackBar(String(err), 'gtw-snackbar--red');
        } else {
          this.gtwSnackbarService.openSnackBar('Expense A&A Configuration Amount(s) have been saved/updated', 'gtw-snackbar--green');
          this.requestGridUpdate.emit('refresh');
        }
        this.cdr.detectChanges();
      },
      error: () => {
        this.isSaveClicked = false;
        this.crudLoading = false;
        this.gtwSnackbarService.openSnackBar('Error occurred while saving. Please try again.', 'gtw-snackbar--red');
        this.cdr.detectChanges();
      }
    });
  }

  deleteExpAnA(): void {
    this.deleteModalConfig = {
      title: 'Remove Configuration',
      message: 'Are you sure to remove this Configuration?',
      iconClass: 'fa fa-sign-out',
      iconColor: 'txt-color-orange-dark',
      confirmButtonText: 'Yes',
      cancelButtonText: 'No',
      confirmButtonClass: 'btn-danger',
      cancelButtonClass: 'btn-default',
      showCancelButton: true,
      showConfirmButton: true,
      animationClass: 'animated fadeIn fast',
      loading: false,
      loadingText: 'Processing...'
    };
    this.showConfirmDialog = true;
    this.cdr.detectChanges();
  }

  onConfirmDelete(): void {
    this.showConfirmDialog = false;
    this.performDeleteExpAnA();
  }

  onCancelDeleteConfirm(): void {
    this.showConfirmDialog = false;
    this.cdr.detectChanges();
  }

  private performDeleteExpAnA(): void {
    const fgnItem = _.find(this.fdiiDerivedData, ['CODE_SHORT_DESC', 'FGN Derived DEI']);
    const giltiItem = _.find(this.fdiiDerivedData, ['CODE_SHORT_DESC', 'GILTI']);
    if (!fgnItem || !giltiItem) return;

    this.isSaveClicked = true;
    this.crudLoading = true;
    this.cdr.detectChanges();

    const clobSettingsObj = { sso_id: this.logged_in_user, scenario: this.scenario };
    const returnObj: any = {
      TAX_YEAR: this.selectedTaxYear,
      ADJ_KEY: this.selectedAdjKey == null ? 0 : this.selectedAdjKey,
      FILING_GRP_KEY: this.selectedFilingGroup,
      GIE_KEY: this.selectedGIECodeKey,
      [String(fgnItem.CODE_KEY) + '_0']: 0,
      [String(giltiItem.CODE_KEY) + '_0']: 0,
      OPERATION_TYPE: 'D'
    };
    const clobDataDetails = [returnObj];
    const baseUrl = this.GENERAL_CONFIG?.base_url || this.baseURLs?.base_url;

    this.expenseConsolConfigService.savePivotedData(
      baseUrl,
      clobSettingsObj,
      clobDataDetails,
      this.globalService,
      this.filterParams,
      this.rowData
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (result: any) => {
        this.isSaveClicked = false;
        this.crudLoading = false;
        const err = result?.errorMessage ?? result?.data?.errorMessage;
        if (err && String(err) !== 'null') {
          this.gtwSnackbarService.openSnackBar(String(err), 'gtw-snackbar--red');
        } else {
          this.gtwSnackbarService.openSnackBar('Expense A&A Configuration Amount(s) have been deleted.', 'gtw-snackbar--green');
          this.requestGridUpdate.emit('refresh');
        }
        this.cdr.detectChanges();
      },
      error: () => {
        this.isSaveClicked = false;
        this.crudLoading = false;
        this.gtwSnackbarService.openSnackBar('Error occurred while deleting. Please try again.', 'gtw-snackbar--red');
        this.cdr.detectChanges();
      }
    });
  }

  /** Normalize option value to string for dropdown binding (edit mode type match). */
  toOptionValue(val: any): string {
    return val != null ? String(val) : '';
  }

  getFilingGroupOptions(): any[] {
    return (this.filingGroupData || []).filter((f: any) => String(f.LABEL || '').toUpperCase() !== 'ALL');
  }
}
